import { apiRequest, apiDownload } from './client.js';

/**
 * Employee CSV bulk-import client (PR ⑧). Upload runs the dry-run server-side
 * and returns the summary; commit inserts the validated rows. CSV downloads
 * (template + error report) go through apiDownload so the Bearer token rides
 * along and the browser gets a save dialog.
 */

export interface ImportSampleRow {
  rowNumber: number;
  employee_number: string;
  name: string;
  hire_date: string;
  gross_salary: string;
  salary_currency: string;
  warning: string | null;
}

export interface ImportRowError {
  row: number;
  category: string;
  message: string;
}

export interface ImportSummary {
  id: string;
  country: string;
  filename: string;
  status: string;
  rowCount: number;
  validRowCount: number;
  errorRowCount: number;
  warningRowCount: number;
  sampleRows: ImportSampleRow[];
  errorsByCategory: Record<string, number>;
  errors?: ImportRowError[];
  committedAt: string | null;
  committedCount: number;
  createdAt: string;
}

export type ImportCountry = 'NG' | 'GH' | 'CD';

export function uploadImport(file: File, country: ImportCountry): Promise<ImportSummary> {
  const form = new FormData();
  form.append('country', country);
  form.append('dryRun', 'true');
  form.append('file', file);
  return apiRequest<ImportSummary>('/v1/employees/imports', { method: 'POST', body: form });
}

export function getImport(id: string): Promise<ImportSummary> {
  return apiRequest<ImportSummary>(`/v1/employees/imports/${encodeURIComponent(id)}`);
}

export function commitImport(id: string, skipErrors: boolean): Promise<{ id: string; status: string; importedCount: number }> {
  return apiRequest(`/v1/employees/imports/${encodeURIComponent(id)}/commit`, { method: 'POST', body: { skipErrors } });
}

export function cancelImport(id: string): Promise<{ id: string; status: string }> {
  return apiRequest(`/v1/employees/imports/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
}

export function downloadErrorReport(id: string): Promise<void> {
  return apiDownload(`/v1/employees/imports/${encodeURIComponent(id)}/errors.csv`, `import-errors-${id}.csv`);
}

export function downloadTemplate(country: ImportCountry): Promise<void> {
  return apiDownload(`/v1/employees/imports/template?country=${country}`, `lumohr-employee-import-template-${country}.csv`);
}

/**
 * The columns the employee-import CSV expects, for the inline disclosure —
 * mirrors ASSET_IMPORT_COLUMNS but is COUNTRY-AWARE (the source of truth is
 * `@kora/migration-csv` columns.ts: `state_code` is required for NG only, and
 * the allowed `salary_currency` differs per country). Keep in sync with it.
 */
const ALLOWED_CURRENCIES: Record<ImportCountry, string> = { NG: 'NGN', GH: 'GHS', CD: 'CDF or USD' };
export function employeeImportColumns(country: ImportCountry): Array<{ name: string; required: string; note: string }> {
  const cols: Array<{ name: string; required: string; note: string }> = [
    { name: 'employee_number', required: 'required', note: '3–32 chars, letters/digits/hyphens, starts alphanumeric. Unique per organisation.' },
    { name: 'first_name', required: 'required', note: '' },
    { name: 'last_name', required: 'required', note: '' },
    { name: 'hire_date', required: 'required', note: 'YYYY-MM-DD.' },
    { name: 'gross_salary', required: 'required', note: 'Positive number, up to 4 decimal places.' },
    { name: 'salary_currency', required: 'required', note: `For ${country}: ${ALLOWED_CURRENCIES[country]}.` },
    { name: 'pay_frequency', required: 'required', note: 'monthly, bi-weekly, or weekly.' },
  ];
  if (country === 'NG') {
    cols.push({ name: 'state_code', required: 'required (NG)', note: 'Nigeria only — the PAYE state (e.g. LA). Not accepted for GH/CD.' });
  }
  cols.push(
    { name: 'effective_from', required: 'optional', note: 'YYYY-MM-DD; defaults to the hire date.' },
    { name: 'email', required: 'optional', note: '' },
    { name: 'phone', required: 'optional', note: '' },
    { name: 'gender', required: 'optional', note: 'male, female, or blank.' },
    { name: 'date_of_birth', required: 'optional', note: 'YYYY-MM-DD.' },
    { name: 'department', required: 'optional', note: '' },
    { name: 'position', required: 'optional', note: '' },
    { name: 'employment_type', required: 'optional', note: 'full_time, part_time, contract, or intern.' },
    { name: 'tax_identifier', required: 'optional', note: '' },
    { name: 'social_security_id', required: 'optional', note: '' },
  );
  return cols;
}
